import React, { useState } from 'react';

import { formatPrice } from 'helpers/utils';
import usePublicApi from 'public-api';
import { useAsyncFunction, useCopyToClipboard } from 'stores/hooks';
import { Button, Icon, Popup } from 'ui';

type Props = {
  ticketId: UUIDField;
  remarketingSettingsId: UUIDField;
  shopName: string;
  color: string;
  textColor: string;
  voucherCode: string | undefined;
  value: number;
  currency: string;
  expirationDate: Date | null;
  isPreview?: boolean;
  min_price?: number;
};

function Coupon({
  ticketId,
  remarketingSettingsId,
  shopName,
  color,
  textColor,
  voucherCode,
  value,
  currency,
  expirationDate,
  isPreview = false,
  min_price,
}: Props) {
  const publicApi = usePublicApi();
  const { copyToClipboard, openCopiedPopup } = useCopyToClipboard();
  const [code, setCode] = useState<string | undefined>(voucherCode);

  const [createVoucher, isCreatingVoucher] = useAsyncFunction(async () => {
    if (isPreview) {
      setCode('R37IN0');
      return;
    }
    const response = await publicApi.MarketplaceRemarketingCreateVoucherApi.create({
      settings_id: remarketingSettingsId,
      request: { ticket_id: ticketId },
    });
    setCode(response.code);
  });

  const hexToRgb = (hexColor: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  };

  const isColorDark = (rgbColor: { r: number; g: number; b: number }) => {
    const y = (0.2126 * rgbColor.r + 0.7152 * rgbColor.g + 0.0722 * rgbColor.b) / 255;
    return y < 0.65;
  };

  const adjustShade = (clr: string, amount: number) =>
    `#${clr
      .replace(/^#/, '')
      .replace(/../g, (clrx) =>
        `0${Math.min(255, Math.max(0, parseInt(clrx, 16) + amount)).toString(16)}`.substring(-2),
      )}`;

  const getBgColor = (hexColor: string) => {
    const rgb = hexToRgb(hexColor);
    if (rgb && !isColorDark(rgb)) {
      return adjustShade(hexColor, -110);
    }

    return '#FFFFFF';
  };

  return (
    <div className="relative flex w-full max-w-2xl items-center justify-between overflow-hidden rounded-md bg-white shadow-lg">
      <div
        className="absolute h-full w-4/5"
        style={{
          backgroundColor: getBgColor(color),
        }}
      />
      <div
        className="absolute h-full w-4/5"
        style={{
          backgroundImage: `linear-gradient(to top right, ${color}b3, ${color})`,
        }}
      />
      <div className="z-10 flex w-2/3 flex-col items-center p-2.5">
        <div
          className="text-center text-sm font-medium"
          style={{
            color: `${textColor}cc`,
          }}
        >
          {gettext('Discount on next purchase')}
        </div>
        <div
          className="text-center text-sm font-medium"
          style={{
            color: textColor,
          }}
        >
          {shopName}
        </div>

        <div className="mt-3 mb-4">
          {!code && (
            <Button onClick={createVoucher} loading={isCreatingVoucher}>
              {gettext('Reveal discount code')}
            </Button>
          )}
          {code && (
            <Popup
              open={openCopiedPopup}
              position="top center"
              trigger={
                <div
                  role="button"
                  tabIndex={0}
                  className="flex items-center justify-center gap-2 rounded-md bg-white/20 px-4 py-2 hover:cursor-pointer hover:bg-white/40"
                  style={{ color: textColor }}
                  onClick={copyToClipboard(code)}
                >
                  <div
                    className="mt-px pt-px text-2xl font-extrabold leading-5 tracking-widest opacity-100"
                    style={{
                      fontFamily: 'monospace',
                    }}
                  >
                    {code}
                  </div>
                  <div
                    className="flex items-center justify-center"
                    style={{
                      color: `${textColor}cc`,
                    }}
                  >
                    <Icon name="clipboard-copy" />
                  </div>
                </div>
              }
            >
              <div className="whitespace-nowrap text-sm text-gray-500">{gettext('Copied to clipboard')}</div>
            </Popup>
          )}
        </div>
        {expirationDate && (
          <div
            className="flex gap-1 self-start text-xs"
            style={{
              color: `${textColor}cc`,
            }}
          >
            <span className="whitespace-nowrap">{gettext('This coupon is valid until')}</span>
            <span className="whitespace-nowrap">{moment(expirationDate).format('l')}</span>
            {min_price && (
              <span className="whitespace-nowrap">
                {gettext('for purchases over')} {formatPrice(min_price, currency)}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="absolute left-2/3 top-1/2 h-52 w-52 -translate-y-1/2 rounded-full bg-white" />
      <div className="text-blackp-2 z-10 flex w-1/3 justify-center text-2xl font-bold sm:text-3xl">
        {formatPrice(value, currency)}
      </div>
    </div>
  );
}

export default Coupon;
